import $, { Cash } from "cash-dom";
import {historyReplace, publish, subscribe} from '@js/humans/helper';
import {HistoryType, InternalHistoryState} from './types/history';
import History from './history';
import qs from 'qs';

type PseudoFilterListingParams = Record<string, string|string[]|null>;

/**
 * PseudoFilter Module
 * Handles filtering of pseudo items based on clicked filter links.
 * NOW ALSO handles filter UI (open/close) - moved from Register for cleaner separation
 *
 * - Reads two URL parameters: "type" and "status" (each a comma‑separated list).
 * - Filters are applied only within the active section (from the closest parent with data-section).
 * - When a filter link is clicked, it toggles its value in the appropriate parameter.
 * - Each filter group is generated in a <ul> that carries a data-filter-type attribute ("type" or "status").
 * - In the active section, teaser list items (with class "js-teaser-list-item")
 *   are processed:
 *     • Their "data-filter-type" attribute is compared against the active type filters.
 *     • Their "data-filter-status" attribute is compared against the active status filters.
 *   A teaser is marked as active (adds "is-active" and removes any data-filtered attribute)
 *   only if it matches both filter sets (or if one of the filter sets is empty).
 *
 * The URL is updated with both "type" and "status" parameters and the active section.
 *
 * @package KAA
 * @author Tim Jungmann
 */
export default class PseudoFilter {
    static #instance: PseudoFilter;
    protected $body: Cash;
    protected listingParams: PseudoFilterListingParams = {};
    protected $teaserListItem: Cash|null = null;

    protected ui = {
        filterForm: '.js-pseudo-filter',
        filterCheckbox: '.js-pseudo-filter-checkbox',
        filterTrigger: '.js-filter-trigger',
        teaserListItem: '.js-teaser-list-item',
        noResultsMessage: '.js-no-results-message',
    };

    protected constructor() {
        this.$body = $("body");
        this.updateListingParamsFromUrl();
        this.registerEvents();
        this.$teaserListItem = $(this.ui.teaserListItem);
        this.checkActiveFilter();
        this.updateFilterActiveState();
        this.filterListings();
    }

    public static getInstance(): PseudoFilter {
        if (!PseudoFilter.#instance) {
            PseudoFilter.#instance = new PseudoFilter();
        }
        return PseudoFilter.#instance;
    }

    protected registerEvents(): void {
        // Handle filter trigger
        this.$body.on("click", this.ui.filterTrigger, (event) => this.toggleFilters(event));

        // Handle filter changes
        this.$body.on('change', this.ui.filterCheckbox, this.onFilterChange.bind(this));

        // Handle history state changes
        subscribe('history.state.pushed', this.onHistoryStateChanged.bind(this));
        subscribe('history.state.replaced', this.onHistoryStateChanged.bind(this));
        subscribe('history.state.popped', this.onHistoryStateChanged.bind(this));
    }

    /**
     * Toggle filter visibility
     */
    protected toggleFilters(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        const $trigger = $(event.currentTarget as HTMLElement);
        const targetId = $trigger.attr("aria-controls");
        const $filter = $(`#${targetId}`);
        const isExpanded = $trigger.attr("aria-expanded") === "true";

        // Only open if not already expanded
        if (!isExpanded) {
            this.openFilter($trigger, $filter);
        }
    }

    /**
     * Open filter
     */
    protected openFilter($trigger: Cash, $filter: Cash): void {
        $trigger.attr("aria-expanded", "true")
                .attr("aria-label", $trigger.attr("aria-label")?.replace("Show", "Hide"));

        $filter.attr("aria-hidden", "false")
               .addClass("is-visible");

        // Focus first interactive element in filter for accessibility
        setTimeout(() => {
            const $firstInput = $filter.find('input, button').first();
            if ($firstInput.length) {
                $firstInput.focus();
            }
        }, 100);
    }

    /**
     * Handles modal relevant history changes
     */
    protected onHistoryStateChanged(topic: string, state: Partial<InternalHistoryState>): void {
        if (!History.isStateOfType(state, HistoryType.Work, topic)) {
            return;
        }

        this.updateListingParamsFromUrl();
        this.updateFilterActiveState();
    }

    /**
     * Uses the listing params from the URL
     */
    protected updateListingParamsFromUrl(): void {
        this.listingParams = Object.fromEntries(
            Object.entries(qs.parse(window.location.search.replace(/^\?+/, '')))
                .map(([key, val]) => [key, typeof val === 'string' ? val.split(',') : val])
        ) as PseudoFilterListingParams;
    }

    /**
     * Returns the proper history URL to set for the given update params
     */
    protected getHistoryUrl(params?: Partial<PseudoFilterListingParams>): string {
        return `${window.location.pathname}?${this.getListingParams(params)}`;
    }

    /**
     * Returns the stringified version for the params object to apply for history URL changes
     */
    protected getListingParams(params?: Partial<PseudoFilterListingParams>): string {
        if (params) {
            this.listingParams = {...this.listingParams, ...params} as PseudoFilterListingParams;
        }

        const filteredParams = Object.fromEntries(
            Object.entries(this.listingParams)
                .filter(([_, val]) => val !== null)
                .map(([key, val]) => [key, Array.isArray(val) ? val.join(',') : val])
        );

        return qs.stringify(filteredParams);
    }

    /**
     * Updates filter state and auto-expands if active
     */
    protected updateFilterActiveState(): void {
        const $filterForms = $(this.ui.filterForm);

        $filterForms.each((_, form) => {
            const $form = $(form);
            const section = $form.data('section');

            // Check if any filters are active for section
            const hasActiveFilters = this.listingParams.filter &&
                Object.entries(this.listingParams.filter).some(([filterGroup, filterArray]) => {
                    // Check if this filter group belongs to the current section
                    const groupSection = filterGroup.split('-')[0];
                    return groupSection === section && Array.isArray(filterArray) && filterArray.length > 0;
                });

            // Toggle is-active class
            $form.toggleClass('is-active', !!hasActiveFilters);

            // Auto-expand if has active filters
            if (hasActiveFilters) {
                const filterId = $form.attr('id');
                const $trigger = $(`[aria-controls="${filterId}"]`);

                if ($trigger.length) {
                    $trigger.attr('aria-expanded', 'true');
                    $form.attr('aria-hidden', 'false').addClass('is-visible');
                }
            }
        });
    }

    /**
     * Render filtered page and update filter counts based on current selected filter
     */
    protected onFilterChange(event: Event): void {
        // Get section from changed checkbox's form
        const $checkbox = $(event.currentTarget as HTMLElement);
        const $form = $checkbox.closest(this.ui.filterForm);
        const section = $form.data('section') as string;

        let filter = {};
        $(this.ui.filterForm).each((_, form) => {
            const $form = $(form);
            const formFilter = qs.parse($form.serialize())?.filter ?? null;
            if (typeof formFilter === 'object' && formFilter !== null) {
                filter = {...filter, ...formFilter};
            }
        });

        // Update history path
        historyReplace({
            type: HistoryType.Work,
            url: this.getHistoryUrl({filter} as unknown as Partial<PseudoFilterListingParams>),
        })

        this.updateFilterActiveState();

        // Apply filtering inside view transition if supported
        // @ts-ignore - View Transitions API
        if (document.startViewTransition && !this.isMobile()) {
            // @ts-ignore
            document.startViewTransition(() => {
                this.filterListings();
            });
        } else {
            this.filterListings();
        }

        // Scroll to filtered section
        if (section) {
            setTimeout(() => {
                this.scrollToList(section);
            }, 50);
        }
    }

    /**
     * Checks active filter set via URL
     */
    protected checkActiveFilter(): void {
        if (!this.listingParams.filter) {
            return;
        }

        const $filterForm = $(this.ui.filterForm);
        if ($filterForm.length === 0) {
            return;
        }

        // Set checkbox to active for current filters
        Object.entries(this.listingParams.filter).forEach(([filterGroup, selectedFilter]) => {
            selectedFilter.forEach((filterValue: number | string) => {
                $filterForm.find(`[data-related-field="${filterGroup}"][value="${filterValue}"]`).prop('checked', true);
            });
        });
    }

    /**
     * Filter listings
     */
    protected filterListings(): void {
        if (!this.listingParams.filter) {
            this.$teaserListItem?.removeClass('is-filtered');
            this.updateNoResultsMessages();
            return;
        }

        // @ts-ignore
        const filter = {...this.listingParams.filter};

        this.$teaserListItem?.each((_, teaserListItem) => {
            const $teaserListItem = $(teaserListItem);
            const section = $teaserListItem.attr('data-filter-section');
            let isVisible = true;

            const filterTypes = ['type', 'status', 'tags'];
            const checks = filterTypes.map(type => ({
                teaserFilters: ($teaserListItem.attr(`data-filter-${type}`) || '')
                    .split(',').map(x => x.trim()).filter(x => x),
                selectedFilters: filter[`${section}-${type}`] || [],
            }));

            checks.forEach(({teaserFilters, selectedFilters}) => {
                if (isVisible && selectedFilters.length > 0) {
                    // @ts-ignore
                    isVisible = selectedFilters.some(item => teaserFilters.includes(item));
                }
            });

            $teaserListItem.toggleClass('is-filtered', !isVisible);
        });

        this.updateNoResultsMessages();
    }

    /**
     * Update no results messages for each section
     */
    protected updateNoResultsMessages(): void {
        const sections = ['buildings', 'projects', 'index'];

        sections.forEach(section => {
            const $message = $(this.ui.noResultsMessage).filter(`[data-section="${section}"]`);

            if ($message.length === 0) {
                return;
            }

            // Count visible items in this section's list
            const $sectionList = $(`#${section}`);
            const visibleCount = $sectionList.find(`${this.ui.teaserListItem}:not(.is-filtered)`).length;

            // Show message if no results, hide if there are results
            $message.attr('aria-hidden', visibleCount > 0 ? 'true' : 'false');
        });
    }

    /**
     * Scroll to list top without triggering view transition
     * Calculates the correct offset to align with sticky headers
     */
    protected scrollToList(sectionId: string): void {
        const $targetSection = $(`#${sectionId}`);

        if (!$targetSection.length) return;

        // Publish anchor scrolling
        publish('anchorScroll.start', true);

        // Calculate accurate offset
        let offsetTop = $targetSection.offset()?.top ?? 0;

        // Create a temporary element to resolve the correct top scroll
        const tempElement = document.createElement("div");
        tempElement.classList.add('register-top-helper');
        $targetSection[0]?.appendChild(tempElement);

        // Get computed height in pixels
        const registerListTopPx = parseFloat(getComputedStyle(tempElement).height);

        // Remove the temporary element after calculation
        tempElement.remove();

        // Add the custom property value to offsetTop
        offsetTop -= registerListTopPx;

        // Instant scroll without view transition
        window.scrollTo({
            top: offsetTop,
            behavior: "instant"
        });

        // Remove anchor scrolling status
        window.setTimeout(() => {
            publish('anchorScroll.end', true);
        }, 50);
    }

    /**
     * Check for breakpoint
     */
    protected isMobile(): boolean {
        return window.innerWidth < 1024;
    }
}